import { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, BookOpen, Layers, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const TYPE_COLORS = {
  Asset: "text-[#2F6B4A]",
  Liability: "text-[#B4283A]",
  Equity: "text-[#7A4A00]",
  Revenue: "text-[#2F4F4F]",
  Expense: "text-[#5C5C5C]",
};

// Stable client-side ID for dynamic form rows (journal-entry lines).
let _rid = 0;
function newRid() { _rid += 1; return `r${_rid}`; }
function withRid(row) { return { _rid: newRid(), ...row }; }

export default function ChartOfAccounts() {
  const [tab, setTab] = useState("coa");
  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Books</div>
        <h1 className="font-display text-4xl mt-2">Chart of Accounts</h1>
        <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
          The scaffold of every report — accounts group your money by kind. Journal entries let you post debits and credits directly.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList data-testid="coa-tabs" className="bg-[var(--hover)]">
          <TabsTrigger value="coa" data-testid="tab-coa">
            <Layers className="w-4 h-4 mr-2" /> Chart of Accounts
          </TabsTrigger>
          <TabsTrigger value="je" data-testid="tab-je">
            <BookOpen className="w-4 h-4 mr-2" /> Journal Entries
          </TabsTrigger>
        </TabsList>
        <TabsContent value="coa"><CoaTab /></TabsContent>
        <TabsContent value="je"><JournalTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

// ---------------- CoA Tab ----------------
function CoaTab() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", type: "Asset", parent_code: "", description: "", is_active: true });
  const [deleteId, setDeleteId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [industryOpen, setIndustryOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coa");
      setAccounts(data);
    } catch (err) {
      console.warn("[CoA] load failed:", err?.message);
      toast.error("Couldn't load chart of accounts");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: "", name: "", type: "Asset", parent_code: "", description: "", is_active: true });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      code: a.code, name: a.name, type: a.type,
      parent_code: a.parent_code || "", description: a.description || "", is_active: a.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code and name are required");
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      type: form.type,
      parent_code: form.parent_code || null,
      description: form.description,
      is_active: form.is_active,
    };
    try {
      if (editing) await api.patch(`/coa/${editing.id}`, payload);
      else await api.post("/coa", payload);
      toast.success(editing ? "Account updated" : "Account created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/coa/${deleteId}`);
      toast.success("Account deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = filter === "all" ? accounts : accounts.filter((a) => a.type === filter);
  const grouped = ["Asset", "Liability", "Equity", "Revenue", "Expense"].map((t) => ({
    type: t,
    items: filtered.filter((a) => a.type === t),
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 h-9" data-testid="coa-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {["Asset", "Liability", "Equity", "Revenue", "Expense"].map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" className="h-10" data-testid="coa-industry-btn"
                onClick={() => setIndustryOpen(true)}>
          <Sparkles className="w-4 h-4 mr-2" /> Apply industry template
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="coa-new-btn" onClick={openCreate}
              className="rounded-md btn-brand h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New account
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit account" : "New account"}</DialogTitle>
              <DialogDescription>Code is unique per entity. Type determines the reporting side.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Code</Label>
                <Input data-testid="coa-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="1100" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="coa-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Asset", "Liability", "Equity", "Revenue", "Expense"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Name</Label>
                <Input data-testid="coa-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Accounts Receivable" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="coa-form-save" onClick={save} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">
                {editing ? "Save" : "Create"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-hidden">
        {grouped.map(({ type, items }) => (
          items.length === 0 ? null : (
            <div key={type}>
              <div className={`px-5 py-2 text-xs uppercase tracking-[0.2em] ${TYPE_COLORS[type]} bg-[var(--hover)]/40`}>
                {type} · {items.length}
              </div>
              <div className="divide-y divide-[var(--divider)]">
                {items.map((a) => (
                  <div key={a.id} data-testid={`coa-row-${a.code}`} className="flex items-center gap-4 px-5 py-3 hover:bg-[var(--row-hover)]">
                    <div className="font-mono text-xs w-14 text-[var(--muted-text)]">{a.code}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{a.name}</div>
                      {a.description && <div className="text-xs text-[var(--muted-text)] truncate">{a.description}</div>}
                    </div>
                    {a.is_default && <span className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">default</span>}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`coa-edit-${a.code}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteId(a.id)} data-testid={`coa-delete-${a.code}`}>
                      <Trash2 className="w-4 h-4 text-[#E63946]" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
        {filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">No accounts.</div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account?</AlertDialogTitle>
            <AlertDialogDescription>Any journal entries referencing this account will fail to save.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="coa-delete-confirm" onClick={remove} className="bg-[#E63946] hover:bg-[#c92e3b] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <IndustryTemplateDialog
        open={industryOpen}
        onClose={() => setIndustryOpen(false)}
        onApplied={() => { setIndustryOpen(false); load(); }}
        existingCodes={new Set(accounts.map((a) => a.code))}
      />
    </div>
  );
}

// ---------------- Industry template dialog ----------------
function IndustryTemplateDialog({ open, onClose, onApplied, existingCodes }) {
  const [industries, setIndustries] = useState([]);
  const [selected, setSelected] = useState(null); // key
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/industries");
        setIndustries(data);
      } catch { toast.error("Couldn't load industry templates"); }
    })();
  }, [open]);

  const loadPreview = async (key) => {
    setSelected(key); setPreview(null);
    try {
      const { data } = await api.get(`/industries/${key}`);
      setPreview(data);
    } catch { toast.error("Couldn't load template preview"); }
  };

  const apply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coa/apply-industry", { industry: selected });
      toast.success(`${data.added} account${data.added === 1 ? "" : "s"} added${data.skipped ? ` · ${data.skipped} already existed` : ""}`);
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't apply template");
    } finally { setApplying(false); }
  };

  const missing = preview ? preview.accounts.filter((a) => !existingCodes.has(a.code)) : [];
  const alreadyThere = preview ? preview.accounts.length - missing.length : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl" data-testid="industry-dialog">
        <DialogHeader>
          <DialogTitle>Apply industry template</DialogTitle>
          <DialogDescription>
            We'll add any missing accounts from the selected template to your Chart of Accounts. Nothing you already have gets renamed or deleted.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 max-h-[520px]">
          {/* Left: template list */}
          <div className="md:col-span-1 border-r border-[var(--border)] pr-3 overflow-y-auto" data-testid="industry-dialog-list">
            {industries.map((it) => (
              <button key={it.key} onClick={() => loadPreview(it.key)}
                      data-testid={`industry-dialog-item-${it.key}`}
                      className={`w-full text-left rounded-md p-2.5 mb-1.5 transition-colors border ${
                        selected === it.key ? "border-[var(--brand)] bg-[var(--brand-light)]" : "border-transparent hover:bg-[var(--hover)]"
                      }`}>
                <div className="text-sm font-medium">{it.label}</div>
                <div className="text-[11px] text-[var(--muted-text)] mt-0.5 line-clamp-2">{it.description}</div>
                <div className="text-[10px] text-[var(--muted-text)] mt-1">{it.account_count} accounts</div>
              </button>
            ))}
          </div>

          {/* Right: preview */}
          <div className="md:col-span-2 overflow-y-auto" data-testid="industry-dialog-preview">
            {!selected ? (
              <div className="text-sm text-[var(--muted-text)] p-6 text-center">Pick an industry to preview the accounts we'd add.</div>
            ) : !preview ? (
              <div className="text-sm text-[var(--muted-text)] p-6 text-center">Loading preview…</div>
            ) : (
              <>
                <div className="mb-3 text-sm">
                  <span className="font-semibold text-[var(--brand)]">{missing.length}</span> new account{missing.length === 1 ? "" : "s"} to add
                  {alreadyThere > 0 && <span className="text-[var(--muted-text)]"> · {alreadyThere} already in your CoA (kept as-is)</span>}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] border-b border-[var(--divider)]">
                      <th className="text-left py-1.5 pl-1">Code</th>
                      <th className="text-left py-1.5">Name</th>
                      <th className="text-left py-1.5">Type</th>
                      <th className="text-left py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.accounts.map((a) => {
                      const has = existingCodes.has(a.code);
                      return (
                        <tr key={a.code} className={`border-b border-[var(--divider)] last:border-none ${has ? "opacity-60" : ""}`}>
                          <td className="pl-1 py-1.5 font-mono text-xs">{a.code}</td>
                          <td className="py-1.5">{a.name}</td>
                          <td className={`py-1.5 text-xs ${TYPE_COLORS[a.type] || ""}`}>{a.type}</td>
                          <td className="py-1.5 text-[10px] uppercase tracking-widest">
                            {has ? <span className="text-[var(--muted-text)]">exists</span>
                                 : <span className="text-[#2F6B4A]">will add</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={onClose} data-testid="industry-dialog-cancel">Cancel</Button>
          <Button className="btn-brand" data-testid="industry-dialog-apply"
                  disabled={!selected || applying || missing.length === 0}
                  onClick={apply}>
            {applying ? "Applying…" : missing.length > 0 ? `Add ${missing.length} account${missing.length === 1 ? "" : "s"}` : "Nothing to add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Journal Entries Tab ----------------
function JournalTab() {
  const [entries, setEntries] = useState([]);
  const { isActive } = useCurrencyRail();
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(newJournalForm());
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: e }, { data: a }] = await Promise.all([
        api.get("/journal-entries"),
        api.get("/coa"),
      ]);
      setEntries(e);
      setAccounts(a);
    } catch (err) {
      console.warn("[Journal] load failed:", err?.message);
      toast.error("Couldn't load journal entries");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  function newJournalForm() {
    return {
      date: format(new Date(), "yyyy-MM-dd"),
      memo: "",
      reference: "",
      currency: "USD",
      lines: [
        withRid({ account_code: "", debit: "", credit: "", memo: "" }),
        withRid({ account_code: "", debit: "", credit: "", memo: "" }),
      ],
    };
  }

  const openCreate = () => { setEditing(null); setForm(newJournalForm()); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      date: e.date,
      memo: e.memo,
      reference: e.reference || "",
      currency: e.currency || "USD",
      lines: e.lines.map((l) => withRid({
        account_code: l.account_code, debit: l.debit || "", credit: l.credit || "", memo: l.memo || "",
      })),
    });
    setOpen(true);
  };

  const totals = form.lines.reduce(
    (acc, l) => ({
      d: acc.d + (parseFloat(l.debit) || 0),
      c: acc.c + (parseFloat(l.credit) || 0),
    }),
    { d: 0, c: 0 }
  );
  const balanced = Math.abs(totals.d - totals.c) < 0.005 && totals.d > 0;

  const save = async () => {
    if (!form.memo.trim()) return toast.error("Memo is required");
    if (!balanced) return toast.error("Debits must equal credits and totals must be > 0");
    const payload = {
      date: form.date,
      memo: form.memo.trim(),
      reference: form.reference,
      currency: form.currency || "USD",
      lines: form.lines
        .filter((l) => l.account_code)
        .map((l) => ({
          account_code: l.account_code,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          memo: l.memo,
        })),
    };
    if (payload.lines.length < 2) return toast.error("A journal needs at least 2 lines");
    try {
      if (editing) await api.patch(`/journal-entries/${editing.id}`, payload);
      else await api.post("/journal-entries", payload);
      toast.success(editing ? "Journal updated" : "Journal posted");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/journal-entries/${deleteId}`);
      toast.success("Journal deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="je-new-btn" onClick={openCreate}
              className="rounded-md btn-brand h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New journal entry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit journal entry" : "New journal entry"}</DialogTitle>
              <DialogDescription>Debits must equal credits. Two or more lines required.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Date</Label>
                <Input type="date" data-testid="je-form-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Reference</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="INV-001" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Memo</Label>
                <Input data-testid="je-form-memo" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="Description" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Currency</Label>
                <Select value={form.currency || "USD"} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="je-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="NGN">NGN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                    <th className="py-2 text-left">Account</th>
                    <th className="py-2 text-left w-40">Memo</th>
                    <th className="py-2 text-right w-32">Debit</th>
                    <th className="py-2 text-right w-32">Credit</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, i) => (
                    <tr key={l._rid} className="border-b border-[var(--divider)]" data-testid={`je-line-${i}`}>
                      <td className="py-2 pr-2">
                        <Select value={l.account_code} onValueChange={(v) => {
                          const next = [...form.lines]; next[i].account_code = v; setForm({ ...form, lines: next });
                        }}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Pick account" /></SelectTrigger>
                          <SelectContent>
                            {["Asset", "Liability", "Equity", "Revenue", "Expense"].map((t) => {
                              const items = accounts.filter((a) => a.type === t);
                              if (items.length === 0) return null;
                              return (
                                <SelectGroup key={t}>
                                  <SelectLabel>{t}</SelectLabel>
                                  {items.map((a) => (
                                    <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>
                                  ))}
                                </SelectGroup>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 pr-2">
                        <Input value={l.memo} onChange={(e) => {
                          const next = [...form.lines]; next[i].memo = e.target.value; setForm({ ...form, lines: next });
                        }} className="h-9" placeholder="—" />
                      </td>
                      <td className="py-2 pr-2">
                        <Input type="number" step="0.01" value={l.debit} onChange={(e) => {
                          const next = [...form.lines]; next[i].debit = e.target.value; if (e.target.value) next[i].credit = ""; setForm({ ...form, lines: next });
                        }} className="h-9 text-right font-mono" data-testid={`je-line-${i}-debit`} />
                      </td>
                      <td className="py-2 pr-2">
                        <Input type="number" step="0.01" value={l.credit} onChange={(e) => {
                          const next = [...form.lines]; next[i].credit = e.target.value; if (e.target.value) next[i].debit = ""; setForm({ ...form, lines: next });
                        }} className="h-9 text-right font-mono" data-testid={`je-line-${i}-credit`} />
                      </td>
                      <td className="py-2">
                        {form.lines.length > 2 && (
                          <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) })}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} className="py-2">
                      <Button size="sm" variant="ghost" data-testid="je-add-line" onClick={() => setForm({ ...form, lines: [...form.lines, withRid({ account_code: "", debit: "", credit: "", memo: "" })] })}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                      </Button>
                    </td>
                    <td className="py-2 text-right font-mono border-t border-[#2F4F4F]" data-testid="je-total-debit">{totals.d.toFixed(2)}</td>
                    <td className="py-2 text-right font-mono border-t border-[#2F4F4F]" data-testid="je-total-credit">{totals.c.toFixed(2)}</td>
                    <td />
                  </tr>
                  <tr>
                    <td colSpan={4} className={`text-right pt-1 text-xs ${balanced ? "text-[#2F6B4A]" : "text-[#B4283A]"}`} data-testid="je-balance-status">
                      {balanced ? "In balance ✓" : `Out of balance by ${(totals.d - totals.c).toFixed(2)}`}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="je-form-save" onClick={save} disabled={!balanced}
                className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">
                {editing ? "Save changes" : "Post journal"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-hidden">
        {(() => { const visible = entries.filter((e) => isActive(e.currency || "USD")); return visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">
            {entries.length === 0 ? "No journal entries yet." : "No entries on the selected currency rail."}
          </div>
        ) : (
          <div className="divide-y divide-[var(--divider)]">
            {visible.map((e) => (
              <div key={e.id} data-testid={`je-row-${e.id}`} className="px-5 py-3 hover:bg-[var(--row-hover)]">
                <div className="flex items-center gap-3">
                  <div className="text-xs text-[var(--muted-text)] font-mono w-24">{e.date}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {e.memo}
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded text-[#FDFCF8]"
                            style={{ backgroundColor: (e.currency || "USD") === "USD" ? "#1E5A96" : "#4C956C" }}>
                        {e.currency || "USD"}
                      </span>
                    </div>
                    {e.reference && <div className="text-xs text-[var(--muted-text)]">{e.reference}</div>}
                  </div>
                  <div className="text-sm font-mono text-[var(--muted-text)]">{e.total_debit.toFixed(2)}</div>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(e)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteId(e.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                </div>
                <div className="mt-2 ml-24 text-xs text-[var(--muted-text)] space-y-0.5">
                  {e.lines.map((l, i) => (
                    <div key={`${e.id}-${i}-${l.account_code}`} className="flex gap-2 font-mono">
                      <span className="w-16 text-right">{l.account_code}</span>
                      <span className="flex-1 truncate">{l.memo}</span>
                      <span className="w-24 text-right">{l.debit ? l.debit.toFixed(2) : ""}</span>
                      <span className="w-24 text-right">{l.credit ? l.credit.toFixed(2) : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ); })()}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete journal entry?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="je-delete-confirm" className="bg-[#E63946] hover:bg-[#c92e3b] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
